import { useMutation, useSuspenseQuery } from '@apollo/client'
import { Loader } from 'lucide-react'
import { Suspense, useState, useTransition } from 'react'
import { toast } from 'sonner'
import { ToasterContent } from '@/components/sv/toaster-content'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { gql } from '@/lib/graphql/gql'
import { useIdCompany } from '@/lib/hooks'
import { DisableSsoDialogFallback } from '@/routes/single-sign-on/sso-config/disable-sso-dialog/fallback'
import type {
  DisableSamlSettingMutation,
  DisableSamlSettingMutationVariables,
  DisableSamlUsersQuery,
  DisableSamlUsersQueryVariables,
} from '@/routes/single-sign-on/sso-config/disable-sso-dialog/index.types.gen'

const DISABLE_SAML_USERS_QUERY = gql`
  query DisableSamlUsers($idCompany: String!) {
    company(id: $idCompany) {
      id
      users {
        id
        user {
          id
          hasPassword
        }
      }
    }
  }
`

const DISABLE_SAML_SETTING_MUTATION = gql`
  mutation DisableSamlSetting($idCompany: String!, $shouldSendEmail: Boolean) {
    disableSamlSetting(
      idCompany: $idCompany
      shouldSendEmail: $shouldSendEmail
    ) {
      id
    }
  }
`

type Props = {
  isOpen: boolean
  onClose: () => void
}

const DisableSsoDialogContent = ({ onClose }: { onClose: () => void }) => {
  const { idCompany } = useIdCompany()
  const [shouldSendEmail, setShouldSendEmail] = useState(false)
  const [isPending, startTransition] = useTransition()

  const { data } = useSuspenseQuery<
    DisableSamlUsersQuery,
    DisableSamlUsersQueryVariables
  >(DISABLE_SAML_USERS_QUERY, {
    variables: { idCompany },
  })

  const [disableSamlSetting] = useMutation<
    DisableSamlSettingMutation,
    DisableSamlSettingMutationVariables
  >(DISABLE_SAML_SETTING_MUTATION)

  const usersWithoutPassword =
    data?.company?.users.filter(
      (companyUser) => !companyUser.user.hasPassword
    ) ?? []

  const handleDisable = () => {
    startTransition(async () => {
      try {
        await disableSamlSetting({
          variables: {
            idCompany,
            shouldSendEmail,
          },
        })
        toast(
          <ToasterContent variant="success">
            SSO has been disabled successfully
          </ToasterContent>,
          { position: 'top-right' }
        )
        onClose()
      } catch {
        toast(
          <ToasterContent variant="error">
            Failed to disable SSO
          </ToasterContent>,
          { position: 'top-right' }
        )
      }
    })
  }

  return (
    <>
      <AlertDialogHeader>
        <AlertDialogTitle>Disable SSO</AlertDialogTitle>
        <AlertDialogDescription>
          Are you sure you want to disable single sign-on? Users will need to
          use their email and password to log in.
        </AlertDialogDescription>
      </AlertDialogHeader>
      {usersWithoutPassword.length > 0 && (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">
            {usersWithoutPassword.length} user
            {usersWithoutPassword.length > 1 ? 's do' : ' does'} not have a
            password set. They will need to create one to log in after SSO is
            disabled.
          </p>
          <div className="flex items-center gap-2">
            <Checkbox
              id="send-email"
              checked={shouldSendEmail}
              onCheckedChange={(checked) =>
                setShouldSendEmail(checked === true)
              }
            />
            <Label htmlFor="send-email" className="text-sm cursor-pointer">
              Send password reset email to users without a password
            </Label>
          </div>
        </div>
      )}
      <AlertDialogFooter>
        <AlertDialogCancel disabled={isPending} onClick={() => onClose()}>
          Cancel
        </AlertDialogCancel>
        <AlertDialogAction disabled={isPending} onClick={handleDisable}>
          {isPending ? <Loader className="animate-spin" /> : null}
          Continue
        </AlertDialogAction>
      </AlertDialogFooter>
    </>
  )
}

export const DisableSsoDialog = ({ isOpen, onClose }: Props) => {
  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <Suspense fallback={<DisableSsoDialogFallback />}>
          <DisableSsoDialogContent onClose={onClose} />
        </Suspense>
      </AlertDialogContent>
    </AlertDialog>
  )
}
